import { FC, useEffect, useMemo } from 'react'
import { motion, LayoutGroup, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'

import {
  Button,
  Popover,
  usePopoverContext,
  Transition,
  Portal,
  Text,
  CloseIconButton,
  Divider,
  useMediaQuery,
  Icon,
  IconButton,
} from '@lounge-fe/ui-kit'

import { BasketProps } from './types'
import { GiftWrap } from './GiftWrap'
import { GiftWrapPlugin, UnderwearCareBagPlugin } from '../../../cart/plugins'
import { useShopify } from '../../../../hooks/useShopify'
import { BasketLines } from './BasketLines'
import classNames from 'classnames'
import { useCart } from '@/features/cart/hooks/useCart'
import { useCartPlugin } from '@/features/cart/hooks/useCartPlugin'
import { useRecentlyAddedProducts } from '@/features/cart/hooks/useRecentlyAddedProducts'
import { useCartContext } from '@/features/cart'
import { ChoiceChipQuickAdd } from './ChoiceChipQuickAdd'
import { DiscountProgress } from './DiscountProgress'
import { UnderwearCareBag } from './UnderwearCareBag'
import { BasketSummary } from './BasketSummary'
import { AdditionalCheckoutButtons } from './AdditionalCheckoutButtons'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { twMerge } from 'tailwind-merge'
import { CartDonation } from './CartDonation'
import { ClaimOfferTrigger } from './ClaimOfferTrigger'
import { Money } from '@/components/Money'
import { ItemAddedPopover } from './ItemAddedPopover'
import { BasketAddonPopover } from './BasketAddonPopover'
import { BASKET_POPOVER_ID } from './hooks/useBasket'

const CheckoutButton: React.FC = () => {
  const { t } = useTranslation()
  const cart = useCart()
  const { currency } = useShopify()

  return (
    <Button
      as="a"
      href="/checkout"
      key="checkout"
      variant="solid"
      className="whitespace-nowrap grow w-full"
    >
      <Icon.Lock width={16} height={16} className="mr-2" />
      {t('cart.checkout')} |&nbsp;
      {cart.data?.totalPrice && (
        <Money
          data={{
            amount: (cart.data?.totalPrice / 100).toString(),
            currencyCode: currency.active,
          }}
        />
      )}
    </Button>
  )
}

interface BasketInnerButtonsProps {
  isAdditionalCheckoutButtonsEnabled: boolean
}

const BasketInnerButtons: React.FC<BasketInnerButtonsProps> = ({
  isAdditionalCheckoutButtonsEnabled,
}) => {
  if (isAdditionalCheckoutButtonsEnabled) {
    return (
      <div className="flex-row w-full">
        <CheckoutButton />
        <AdditionalCheckoutButtons />
      </div>
    )
  }

  return <CheckoutButton />
}

const BasketInner: FC<BasketProps> = ({
  emptyBagAction,
  choiceChipQuickAdd,
  cartDonation,
  cartDiscountProgress,
  claimOfferTrigger,
  infoTooltip,
}) => {
  const { onClose, onOpen, isOpen } = usePopoverContext()
  const { t } = useTranslation()
  const cart = useCart()
  const plugin = useCartPlugin()
  const { recentlyAddedTimer } = useCartContext()
  const { getFlag } = useFeatureFlags()

  const isAdditionalCheckoutButtonsEnabled = getFlag(
    'cart_additional_checkout_buttons',
  )

  const isBasketAddonsV2Enabled = getFlag('basket_addons_v2')

  const { recentlyAddedProductIds, clearRecentlyAddedProductIds } =
    useRecentlyAddedProducts()

  const hasRecentlyAdded = recentlyAddedProductIds.length > 0

  const {
    itemCount = 0,
    itemsSubtotalPrice = 0,
    totalPrice = 0,
    totalDiscount = 0,
  } = cart?.data || {}

  const isXLDesktop = useMediaQuery(
    ({ screens }) => `(min-width: ${screens['2xl']})`,
  )

  useEffect(() => {
    if (isXLDesktop) {
      hasRecentlyAdded ? onOpen() : onClose()
    }
  }, [hasRecentlyAdded, isXLDesktop])

  const isGiftWrapEnabled = useMemo(
    () => plugin(GiftWrapPlugin)?.enabled || false,
    [],
  )

  const isUnderwearCareBagPluginEnabled = useMemo(
    () => plugin(UnderwearCareBagPlugin)?.enabled || false,
    [],
  )

  useEffect(() => {
    if (!isOpen) {
      recentlyAddedTimer.resume()
    }
  }, [isOpen])

  const iconPadding =
    itemCount >= 10 ? 'pr-8' : itemCount > 0 ? 'pr-7' : undefined

  const iconWidth =
    itemCount >= 10 ? 'w-11' : itemCount > 0 ? 'w-10' : undefined

  return (
    <>
      <Popover.Trigger>
        <IconButton
          aria-label="Basket"
          className={iconWidth}
          icon={
            <div
              className={classNames(
                'cart-drawer__button flex gap-1 items-center cursor-pointer',
                iconPadding,
              )}
            >
              <Icon.Bag width={16} height={16} />
              {itemCount > 0 && (
                <span className="typography typography--body-sm relative">
                  {itemCount >= 99 ? '99+' : itemCount}
                </span>
              )}
            </div>
          }
          onClickCapture={() => {
            clearRecentlyAddedProductIds()
          }}
        />
      </Popover.Trigger>
      <Portal>
        <Popover.Backdrop className="z-40" />
        <Transition.Slide
          direction="right"
          className={classNames(
            'basket-drawer fixed top-0 right-0 bottom-0 z-50 w-full max-w-full sm:max-w-[498px] overflow-hidden top-0',
          )}
          data-testid="basket-desktop-transition"
        >
          <Popover.Content
            className="flex flex-col w-full h-full bg-white relative"
            onMouseEnter={recentlyAddedTimer.pause}
            onMouseLeave={recentlyAddedTimer.start}
          >
            <div className="flex justify-between py-4 border-b border-border-default px-5 2xl:px-6 2xl:pt-8 2xl:pb-7">
              <Text variant="body-lg" className="capitalize font-regular">
                {t('cart.my_bag')}
              </Text>
              <CloseIconButton
                className="!bg-transparent hover:bg-transparent"
                aria-label="Close bag"
                onClick={onClose}
              />
            </div>
            {cartDiscountProgress && (
              <DiscountProgress
                className="px-5 2xl:px-6"
                minimumSpend={cartDiscountProgress.minimumSpend}
                eligibleText={cartDiscountProgress.eligibleText}
                eligibleUrl={cartDiscountProgress.eligibleUrl}
                eligibleUrlText={cartDiscountProgress.eligibleUrlText}
                eligibleCollections={cartDiscountProgress.eligibleCollections}
                ineligibleText={cartDiscountProgress.ineligibleText}
                showOnComplete={cartDiscountProgress.showOnComplete}
                infoTooltip={infoTooltip}
              />
            )}
            {claimOfferTrigger && (
              <ClaimOfferTrigger
                triggerText={claimOfferTrigger.triggerText}
                link={claimOfferTrigger.link}
                backgroundColor={claimOfferTrigger.backgroundColor}
                images={claimOfferTrigger.images}
              />
            )}
            <LayoutGroup>
              <motion.div
                layoutScroll
                className={twMerge([
                  'relative flex flex-col flex-1 overflow-y-auto gap-y-4 mb-4',
                  cartDonation && 'mb-0',
                ])}
              >
                <BasketLines className="mx-5 my-3 2xl:my-4 2xl:mx-6 2xl:mb-0" />
                {choiceChipQuickAdd && (
                  <motion.div
                    layout="position"
                    className="flex-none mx-5 mb-6 2xl:mx-6"
                  >
                    <Divider className="mb-6" />
                    <ChoiceChipQuickAdd {...choiceChipQuickAdd} />
                  </motion.div>
                )}
                {Boolean(itemCount) && (
                  <>
                    {!isBasketAddonsV2Enabled && (
                      <div className="grid gap-4 mx-5 2xl:mx-6">
                        <Divider />
                        {isGiftWrapEnabled && <GiftWrap />}
                        {isUnderwearCareBagPluginEnabled && (
                          <UnderwearCareBag />
                        )}
                      </div>
                    )}
                    <BasketSummary
                      itemCount={itemCount}
                      itemsSubtotalPrice={itemsSubtotalPrice}
                      totalDiscount={totalDiscount}
                      totalPrice={totalPrice}
                    />
                  </>
                )}
                {cartDonation && <CartDonation {...cartDonation} />}
              </motion.div>
            </LayoutGroup>

            <div className="flex flex-col z-20 bg-white ">
              {isBasketAddonsV2Enabled && Boolean(itemCount) && (
                <BasketAddonPopover />
              )}
              <div
                className={twMerge(
                  'flex md:gap-y-4 md:gap-x-6 py-4 px-5 2xl:px-6 2xl:pt-6 2xl:pb-8 sm:[&_>_*]:flex-1 bg-white',
                  isAdditionalCheckoutButtonsEnabled ? 'pb-0' : undefined,
                )}
              >
                {itemCount > 0 || !emptyBagAction ? (
                  <BasketInnerButtons
                    isAdditionalCheckoutButtonsEnabled={
                      isAdditionalCheckoutButtonsEnabled
                    }
                  />
                ) : (
                  <Button
                    as="a"
                    href={emptyBagAction.url}
                    key="empty-bag"
                    variant="solid"
                  >
                    {emptyBagAction.text}
                  </Button>
                )}
              </div>
            </div>
          </Popover.Content>
        </Transition.Slide>
      </Portal>
    </>
  )
}

export const Basket: FC<BasketProps> = ({ ...props }) => {
  return (
    <Popover id={BASKET_POPOVER_ID}>
      <BasketInner {...props} />
    </Popover>
  )
}
